import { useEffect, useRef, useState } from "react";
import { Carousel } from "ancestry-ui-react";
import { useFormStore } from "@/widgets/classic-form/form";
import type { PersonSuggestionItem } from "@/entities/tree-person";
import { QuickFillItem } from "./QuickFillItem";
import styles from "./QuickFillCarousel.module.scss";

interface QuickFillCarouselProps {
    suggestions: PersonSuggestionItem[];
    treeId: string;
    onSelect: (suggestion: PersonSuggestionItem, treeId: string) => void;
}

export const QuickFillCarousel = ({ suggestions, treeId, onSelect }: QuickFillCarouselProps) => {
    const repopulateRef = useRef<(() => void) | null>(null);
    const selectedPersonId = useFormStore((s) => s.values.treePerson.personId);
    const [currentSlide, setCurrentSlide] = useState(0);

    // When the suggestions list re-fetches (e.g. after name fields update), the selected
    // person may shift to a different position or off-screen. Scroll the carousel to keep
    // it visible. We intentionally depend only on `suggestions` — not `selectedPersonId` —
    // so that clicking an already-visible slide doesn't cause an unwanted scroll jump.
    useEffect(() => {
        repopulateRef.current?.();
        if (!selectedPersonId) {
            setCurrentSlide(0);
            return;
        }
        const idx = suggestions.findIndex((s) => s.PersonId === selectedPersonId);
        setCurrentSlide(idx >= 0 ? idx : 0);
    }, [suggestions]);

    return (
        <Carousel
            className={styles.carousel}
            padded
            fullWidth={false}
            currentSlide={currentSlide}
            noFocusChange
            repopulateSlides={(repopulate) => {
                repopulateRef.current = repopulate;
            }}
        >
            {suggestions.map((suggestion) => (
                <Carousel.Slide key={suggestion.PersonId}>
                    <QuickFillItem suggestion={suggestion} treeId={treeId} onSelect={onSelect} />
                </Carousel.Slide>
            ))}
        </Carousel>
    );
};
